import React, { useEffect, useRef, useState, useCallback, useMemo, use } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Room, RoomEvent, Track, VideoPresets, ConnectionState, ReconnectPolicy, ReconnectContext, RoomConnectOptions, RoomOptions, DataPacket_Kind } from 'livekit-client';
import { toggleMic, toggleCamera, toggleScreenShare } from '../../../MediaControls/store/mediaControlSlice';
import { toggleLivekitMic, toggleLivekitCamera, toggleLivekitScreenShare, setConnectionQuality, HALO_LEVEL } from '../../store/livekitSlice/livekitSlice';
import { useMicrophoneTrack } from '../../../../hooks/useMicrophoneTrack';
import { useCameraTrack } from '../../../../hooks/useCameraTrack';
import { useCameraEffect } from '../../../../hooks/useCameraEffect';
import { getRoomConfig } from '../../store/livekitSlice/livekitThunks';
import { useRemoteAudioTracks } from '../../hooks/useRemoteAudioTracks';
import MediaDisplay from '../MediaDisplay/MediaDisplay.jsx';
import AudioPlayback from '../AudioPlayback/AudioPlayback';
import { mapSocketFactory } from '../../../../utils/socketFactory';
import { debouncer } from '../../../../utils/debouncer';
import { set } from 'date-fns';
import { ExponentialBackoff } from "../../../../utils/exponentialBackoff";
import VoiceTranscription from '../VoiceTranscription/VoiceTranscription';
import { useTrackSubscription } from '../../hooks/useTrackSubscription.ts';
import { setTestModal } from '../../../ControlPanel/store/controlPanelSlice.js';
import { tenantHasFeature } from '../../../Auth/utils/AuthUtils.js';
import { addActiveGame, setIncomingInvite, clearRoomGames, removeActiveGame, clearSurveyData, castVote } from "../../../Games/store/GamesSlice.js";
import { closeSessionModal, setSessionView } from '../../../ProductivitySession/store/ProductivitySessionSlice.js';
import { livekitRoomRef } from '../../../../utils/livekitRef.js';

export default function LivekitRoomComponent() {

  // INIT
  const dispatch = useDispatch();

  const [roomState, setRoomState] = useState(false);


  // Livekit URL
  const {
    livekitUrl,
  } = useSelector((state) => state.auth);

  // Redux state
  const {
    livekitMicActive,
    livekitCameraActive,
    livekitScreenShareActive,
    livekitToken
  } = useSelector((state) => state.livekit);

  const { activeGames, surveyDataToPublish } = useSelector((state) => state.games);
  const activeGamesRef = useRef(activeGames);
  const { localAvatar } = useSelector((state) => state.auth);

  const { sessionView } = useSelector((state) => state.productivitySession);
  const [remoteParticipants, setRemoteParticipants] = useState(new Map());

  const livekitMicRef = useRef(livekitMicActive);
  const livekitCameraRef = useRef(livekitCameraActive);
  const livekitScreenShareRef = useRef(livekitScreenShareActive);

  useEffect(() => {
    // On every render, put the latest value from Redux into our refs.
    livekitMicRef.current = livekitMicActive;
    livekitCameraRef.current = livekitCameraActive;
    livekitScreenShareRef.current = livekitScreenShareActive;
    // This ensures that the refs always have the latest state without needing to re-render.

  }, [livekitCameraActive, livekitMicActive, livekitScreenShareActive]);

  const {
    selectedMic,
    selectedCamera,
    selectedEffect,
    selectedBackground,
    cameraActive,
    screenShareActive,
    micLoading,
    cameraLoading,
    screenShareLoading
  } = useSelector((state) => state.mediaControls);

  const { currentRoom, mapConfig } = useSelector((state) => state.MapDisplay);

  /**
   * Room connection options
   * @type {RoomConnectOptions}
   */
  const roomConnectOptions = {
    maxRetries: Infinity,
    autoSubscribe: false
  }

  /**
   * Reconnect policy with exponential backoff
   * @type {ReconnectPolicy}
   */
  const reconnectPolicy = {
    // Exponential backoff, infinite retries
    /**
     * 
     * @param {ReconnectContext} reconnectContext 
     * @returns {number | null} Next retry delay in milliseconds, or null to stop retrying
     */
    nextRetryDelayInMs(reconnectContext) {
      return Math.min(300 * 2 ** reconnectContext.retryCount, 7000); // Cap at 30 seconds
    }
  }

  /**
   * Room options
   * @type {RoomOptions}
   */
  const roomOptions = {
    stopLocalTrackOnUnpublish: false, // Ensures this component has control over track cleanup
    adaptiveStream: false,
    dynacast: true,
    publishDefaults: {
      videoCodec: "vp9",
    },
    videoCaptureDefaults: {
      resolution: VideoPresets.h720.resolution,
    },
    reconnectPolicy: reconnectPolicy,
  };

  // Room and connection state
  const room = useMemo(() => new Room(roomOptions), []);


  const groupToggleMic = useCallback((state) => {
    dispatch(toggleLivekitMic(state));
    dispatch(toggleMic(state));
  }, [dispatch]);

  const groupToggleCamera = useCallback((state) => {
    dispatch(toggleLivekitCamera(state));
    dispatch(toggleCamera(state));
  }, [dispatch]);

  const groupToggleScreenShare = useCallback((state) => {
    dispatch(toggleLivekitScreenShare(state));
    dispatch(toggleScreenShare(state));
  }, []);

  const mediaPersistenceRef = useRef(localAvatar?.media_persistence);
  useEffect(() => {
    mediaPersistenceRef.current = localAvatar?.media_persistence;
  }, [localAvatar?.media_persistence]);

  useEffect(() => {
    groupToggleScreenShare(false);
    if (mediaPersistenceRef.current === 0) {
      groupToggleMic(false);
      groupToggleCamera(false);
    }
  }, [currentRoom, groupToggleMic, groupToggleCamera, groupToggleScreenShare]);



  // Helper function to get currently published tracks by source - FIXED: Stable reference
  const getPublishedTracksBySource = useCallback((sourceType) => {
    if (!room?.localParticipant) return [];

    const tracks = [];
    if (sourceType === Track.Source.Microphone) {
      room.localParticipant.audioTrackPublications.forEach((pub) => {
        if (pub.source === sourceType && pub.track) {
          tracks.push(pub);
        }
      });
    } else {
      room.localParticipant.videoTrackPublications.forEach((pub) => {
        if (pub.source === sourceType && pub.track) {
          tracks.push(pub);
        }
      });
    }
    return tracks;
  }, [room]);

  // Helper function to cleanup tracks by source - FIXED: Stable reference
  const cleanupTracksBySource = useCallback(async (sourceType, excludeTrackSid = null) => {
    if (!room?.localParticipant) return;

    const tracksToUnpublish = [];
    const publicationsToCheck = sourceType === Track.Source.Microphone
      ? room.localParticipant.audioTrackPublications
      : room.localParticipant.videoTrackPublications;

    publicationsToCheck.forEach((pub) => {
      if (pub.source === sourceType && pub.trackSid !== excludeTrackSid && pub.track) {
        tracksToUnpublish.push(pub.track);
      }
    });

    if (tracksToUnpublish.length > 0) {
      console.log(`Cleaning up ${tracksToUnpublish.length} old ${sourceType} track(s)`);
      try {
        await room.localParticipant.unpublishTracks(tracksToUnpublish);
      } catch (error) {
        console.error(`Error cleaning up old ${sourceType} tracks:`, error);
      }
    }
  }, [room]);

  // Fetch Livekit token on room change
  const [connectionError, setConnectionError] = useState(null);

  useEffect(() => {
    dispatch(getRoomConfig());
  }, [currentRoom, livekitUrl]);

  // Helper function to set metadata with retry logic
  const setMetadataWithRetry = useCallback(() => {
    const participantMetadata = {
      name: localAvatar?.display_name || localAvatar?.name || localAvatar?.firstName + ' ' + localAvatar?.lastName,
      urlphoto: localAvatar?.urlphoto,
      email: localAvatar?.email,
      id: localAvatar?.id
    };
    const metadataString = JSON.stringify(participantMetadata);

    console.log('[METADATA] Target metadata:', participantMetadata);

    const backoff = new ExponentialBackoff({
      maxRetries: 8,
      initialInterval: 100,
      maxDelay: 2000,
      onRetry: (error, attempt, delay) => {
        console.log(`[METADATA] Retry attempt ${attempt} after ${delay}ms`);
      }
    });

    const { promise } = backoff.execute(async (attempt) => {
      if (!room.localParticipant) {
        throw new Error("Local participant not ready");
      }

      const currentMetadata = room.localParticipant.metadata;
      console.log(`[METADATA] Attempt ${attempt + 1}: Current metadata:`, currentMetadata);

      if (currentMetadata === metadataString) {
        console.log('[METADATA] ✓ Metadata already synchronized');
        return;
      }

      console.log('[METADATA] Metadata not synchronized, firing update...');
      room.localParticipant.setMetadata(metadataString);
      throw new Error("Metadata not yet synchronized");
    });

    promise.catch(error => {
      console.error('[METADATA] ✗ Failed to synchronize metadata after all retries:', error);
    });
  }, [localAvatar?.display_name, localAvatar?.name, localAvatar?.firstName, localAvatar?.lastName, localAvatar?.urlphoto, localAvatar?.email, room]);

  // Ref to store the latest metadata update function
  const setMetadataWithRetryRef = useRef(setMetadataWithRetry);

  // Update ref and call immediately when localAvatar changes
  useEffect(() => {
    setMetadataWithRetryRef.current = setMetadataWithRetry;
    // Immediately call with latest values if room is connected
    if (room?.localParticipant && room?.connected) {
      setMetadataWithRetryRef.current();
    }
  }, [setMetadataWithRetry, room]);


  const connectionInstanceRef = useRef(0);

  useEffect(() => {
    if (!livekitToken || !livekitUrl || !room) return;
    const currentConnectionInstance = ++connectionInstanceRef.current;

    const handleLocalTrackUnpublished = (publication, participant) => {
      if (connectionInstanceRef.current !== currentConnectionInstance) return;
      console.log(`Track unpublished: ${publication.kind}, source: ${publication.source}, sid: ${publication.trackSid}`);
    };



    // Set up event listeners before connecting
    const onConnected = async () => {
      if (connectionInstanceRef.current !== currentConnectionInstance) return;
      console.log('Connected to LiveKit room: ', room.getSid());
      setConnectionError(null);
      setRoomState(true);
      console.log("setting remote participants", room.remoteParticipants);
      setRemoteParticipants(prev => new Map(room.remoteParticipants));

      // Use ref to get latest metadata update function
      setMetadataWithRetryRef.current();
    };

    const onDisconnected = () => {
      if (connectionInstanceRef.current !== currentConnectionInstance) return;
      console.log('Disconnected from LiveKit room');
      setRoomState(false);
      setRemoteParticipants(null);

    };

    const onConnectionQualityChanged = (quality, participant) => {
      if (connectionInstanceRef.current !== currentConnectionInstance) return;
      dispatch(setConnectionQuality(quality));
    };

    const updateParticipants = () => {
      if (connectionInstanceRef.current !== currentConnectionInstance) return;
      setRemoteParticipants(prev => new Map(room.remoteParticipants));
    }

    let redundancyInterval = setInterval(() => {
      updateParticipants();
    }, 2000);

    const connectToRoom = async () => {
      if (connectionInstanceRef.current !== currentConnectionInstance) return;
      try {

        // If already connected, cleanup first
        if (room.state === ConnectionState.Connected || room.state === ConnectionState.Connecting)
          await cleanup();

        room.on(RoomEvent.Connected, onConnected);
        room.on(RoomEvent.Disconnected, onDisconnected);
        room.on(RoomEvent.LocalTrackUnpublished, handleLocalTrackUnpublished);
        room.on(RoomEvent.ConnectionQualityChanged, onConnectionQualityChanged);
        room.on(RoomEvent.ParticipantConnected, updateParticipants)
        room.on(RoomEvent.ParticipantDisconnected, updateParticipants)
        // Connect to the room
        await room.connect(livekitUrl, livekitToken, roomConnectOptions);
        console.log("Connected to room: ", room.getSid());
      } catch (error) {
        console.error('Failed to connect to LiveKit room:', error);
        throw error;
      }
    };
    // Cleanup function
    const cleanup = async () => {
      if (connectionInstanceRef.current !== currentConnectionInstance) return;
      console.log("Cleaning up room connection...");
      room.off(RoomEvent.Connected, onConnected);
      room.off(RoomEvent.Disconnected, onDisconnected);
      room.off(RoomEvent.LocalTrackUnpublished, handleLocalTrackUnpublished);
      room.off(RoomEvent.ConnectionQualityChanged, onConnectionQualityChanged);
      room.off(RoomEvent.ParticipantConnected, updateParticipants)
      room.off(RoomEvent.ParticipantDisconnected, updateParticipants)
      // Unpublish all tracks before disconnecting
      cleanupTracksBySource(Track.Source.Microphone);
      cleanupTracksBySource(Track.Source.Camera);
      cleanupTracksBySource(Track.Source.ScreenShare);
      setRemoteParticipants(null);
      console.log("Disconnecting from room...");
      setRoomState(false);
      await room.disconnect();
    };

    // TODO: stabelize this
    const attemptRoomConnection = () => {
      if (connectionInstanceRef.current !== currentConnectionInstance) return;
      try {
        const { promise, cancel } = ExponentialBackoff.execute(async () => {
          // Cleanup proactively before each connection attempt
          if (connectionInstanceRef.current !== currentConnectionInstance) return Promise.reject(new Error("Connection instance invalidated"));
          if (room?.state === ConnectionState.Connected || room?.state === ConnectionState.Connecting) await cleanup();
          await connectToRoom();
        });
        promise.catch((e) => {
          console.error("Exponential backoff connection error:", e);
          setConnectionError(e.message);
        })
      }
      catch (e) {
        console.error("Error in attemptRoomConnection:", e);
      }
    }

    connectToRoom();


    // Cleanup
    return () => {
      // Invalidate this connection instance
      connectionInstanceRef.current++;
      if (redundancyInterval) clearInterval(redundancyInterval);
    };
  }, [livekitToken, livekitUrl, cleanupTracksBySource, room]);


  useTrackSubscription(room);


  // Screen share track
  const [screenShareTrack, setScreenShareTrack] = useState(null);

  // Refs for camera effects processing
  const hiddenVideoRef = useRef(null);
  const hiddenCanvasRef = useRef(null);
  const backgroundImageRef = useRef(null);
  const [isImageReady, setIsImageReady] = useState(false);

  // Audio track from microphone
  const { track: audioTrack, error: microphoneTrackError } = useMicrophoneTrack(selectedMic, livekitMicActive);


  const prevRoomIdRef = useRef(null);

  // Handle microphone track error
  useEffect(() => {
    if (microphoneTrackError) {
      console.error('Microphone track error:', microphoneTrackError);
      // If there's an error with the microphone track, stop the track and update UI state
      if (microphoneTrackError.message === "Microphone permission denied") {
        dispatch(setTestModal("mic_permission"));
      }
      groupToggleMic(false);
    }
  }, [microphoneTrackError]);


  // Monitor audio level for halo
  useEffect(() => {
    if (!room || !room.localParticipant || !livekitMicActive || !audioTrack || !roomState) {
      return;
    }

    const interval = setInterval(() => {
      const audioLevel = room.localParticipant?.audioLevel;
      const clampedLevel = Math.max(0, Math.min(1, audioLevel));

      // Map from 0-1 to 1-8
      // Formula: output = (input * (max - min)) + min
      // Where input range is 0-1, output range is 1-8
      const mappedValue = (clampedLevel * 7) + 1;

      // Round up to nearest integer
      const haloLevel = Math.ceil(mappedValue);
      // Check if the socket is connected, if it is, emit if the halo level is higher than 1
      if (mapSocketFactory && mapSocketFactory?.socket && mapSocketFactory?.socket?.connected && haloLevel > 1) {
        mapSocketFactory.socket.emit(`haloLevel`, { level: haloLevel, user_id: localAvatar?.id });
      }
    }, 333); // 3Hz

    // Cleanup interval on unmount
    return () => {
      clearInterval(interval);
    };
  }, [room?.localParticipant, livekitMicActive, audioTrack, localAvatar?.id, roomState, mapConfig]);





  // Raw camera track
  const { track: rawCameraTrack, error: cameraTrackError } = useCameraTrack(selectedCamera, livekitCameraActive);


  useEffect(() => {
    if (cameraTrackError) {
      console.error('Camera track error:', cameraTrackError);
      if (cameraTrackError.message === "Camera permission denied") {
        dispatch(setTestModal("camera_permission"));
      }
      // If there's an error with the camera track, stop the track and update UI state
      groupToggleCamera(false);

    }
  }, [cameraTrackError, groupToggleCamera]);



  // Processed camera track state
  const [processedCameraTrack, setProcessedCameraTrack] = useState(null);




  useEffect(() => {
    console.log('Room state changed:', room?.state, 'isRoomConnected:', roomState);
  }, [room?.state, roomState]);

  useEffect(() => {
    if (audioTrack) {
      console.log('🎤 Audio track created:', audioTrack.id, 'readyState:', audioTrack.readyState);

      // Listen for track ending
      audioTrack.onended = () => {
        console.log('💀 Audio track ENDED:', audioTrack.id);
        console.trace('Track ended stack trace'); // This will show you WHERE it was killed
      };
    }
  }, [audioTrack]);

  // Callback for camera effects - FIXED: Stable reference
  const handleStreamReady = useCallback((stream) => {
    const newTrack = stream ? stream.getVideoTracks()[0] : null;
    setProcessedCameraTrack(newTrack);
  }, []);

  // Apply camera effects
  const { error: cameraEffectError } = useCameraEffect({
    track: rawCameraTrack,
    effect: selectedEffect,
    videoRef: hiddenVideoRef,
    canvasRef: hiddenCanvasRef,
    onStreamReady: handleStreamReady,
    frameSkip: 3,
    backgroundImageRef,
    isImageReady
  });

  useEffect(() => {
    if (cameraEffectError) {
      console.error('Camera effect error:', cameraEffectError);
      // If there's an error with the camera effect, stop the track and update UI state
      groupToggleCamera(false);
    }
  }, [cameraEffectError, groupToggleCamera]);


  // Remote video tracks management
  const onScreenSharePublished = () => {
    // This callback is triggered when a new remote screen share track is published
    // If screenshare is active locally, we deactivate it to ensure only one screen share is active at a time
    if (livekitScreenShareActive) {
      console.log('Remote screen share track published, deactivating local screen share');
      groupToggleScreenShare(false);
    }
  }
  //const { remoteTracks: remoteVideoTracks, remoteParticipants} = useRemoteVideoTracks(room, onScreenSharePublished);

  // --- NEW: Remote audio tracks management ---
  const remoteAudioTracks = useRemoteAudioTracks(room);

  // Screen share acquisition
  useEffect(() => {
    const acquireScreenShare = async () => {
      if (livekitScreenShareActive && !screenShareTrack) {
        try {
          const stream = await navigator.mediaDevices.getDisplayMedia({
            video: true ,
            audio: true, 
          });
          const videoTrack = stream.getVideoTracks()[0];
          setScreenShareTrack(stream);
          // Handle screen share ending
          stream.getVideoTracks()[0].onended = () => {
            // If screen share track ends, revert state and UI
            console.log('Screen share ended');
            groupToggleScreenShare(false);
            setScreenShareTrack(null);
          };
        } catch (error) {
          console.error('Failed to acquire screen share:', error);
          // If screen share fails, revert state
          groupToggleScreenShare(false);
          setScreenShareTrack(null);
        }
      } else if (!livekitScreenShareActive && screenShareTrack) {
        screenShareTrack.getTracks().forEach(track => track.stop());
        setScreenShareTrack(null);
      }

    };

    acquireScreenShare();
    // return a cleanup function to stop the track  listners if it exists
    return () => {
      if (screenShareTrack) {
        screenShareTrack.onended = null; // Remove the onended listener
        setScreenShareTrack(null); // Clear the state
      }
    };
  }, [livekitScreenShareActive, screenShareTrack]);


  // Manage audio track publishing
  useEffect(() => {
    const manageAudioTrack = async () => {
      if (livekitMicActive && audioTrack && roomState) {
        // Proactive cleanup before publishing
        await cleanupTracksBySource(Track.Source.Microphone);
        try {
          await room.localParticipant.publishTrack(audioTrack, {
            name: 'microphone',
            source: Track.Source.Microphone
          });
          // SUCCESS: Update UI state to ON
          if (livekitMicRef.current === true)
            groupToggleMic(true);
        } catch (error) {
          console.error('Failed to publish audio track:', error);
          // FAILURE: Revert both livekit and UI states to OFF
          groupToggleMic(false);
          dispatch(setTestModal("livekit_tester"));

        }
      } else if (!livekitMicActive) {
        groupToggleMic(false);
        await cleanupTracksBySource(Track.Source.Microphone);
      }
    };
    manageAudioTrack();
  }, [room, livekitMicActive, audioTrack, roomState]);

  // Manage camera track publishing
  useEffect(() => {
    const manageCameraTrack = async () => {
      const currentCameraPublications = getPublishedTracksBySource(Track.Source.Camera);
      const currentCameraPublication = currentCameraPublications[0] || null;
      const currentPublishedTrack = currentCameraPublication?.track;

      if (livekitCameraActive && processedCameraTrack && roomState) {
        if (!currentPublishedTrack) {
          // Proactive cleanup before initial publish
          await cleanupTracksBySource(Track.Source.Camera);

          try {
            console.log('publishing camera track', processedCameraTrack);
            await room.localParticipant.publishTrack(processedCameraTrack, {
              name: 'camera',
              source: Track.Source.Camera
            });
            // SUCCESS: Update UI state to ON, check if livekitCameraRef is true to avoid state mismatch
            if (livekitCameraRef.current === true)
              groupToggleCamera(true);
          } catch (error) {
            console.error('Failed to publish camera track:', error);
            // FAILURE: Revert both livekit and UI states to OFF
            groupToggleCamera(false);
            dispatch(setTestModal("livekit_tester"));

          }
        } else if (currentPublishedTrack.mediaStreamTrack !== processedCameraTrack && roomState) {
          try {
            console.log('replacing camera track', currentPublishedTrack.mediaStreamTrack, 'with', processedCameraTrack);
            await currentPublishedTrack.replaceTrack(processedCameraTrack);
            // SUCCESS: Update UI state to ON, check if livekitCameraRef is true to avoid state mismatch
            if (livekitCameraRef.current === true)
              groupToggleCamera(true);
          } catch (error) {
            console.error('Failed to replace camera track:', error);
            // FAILURE: Revert both livekit and UI states to OFF
            groupToggleCamera(false);
          }
        }
      } else if (!livekitCameraActive && currentPublishedTrack) {
        try {
          console.log('unpublishing camera track', currentPublishedTrack);
          groupToggleCamera(false);
          await cleanupTracksBySource(Track.Source.Camera);
        } catch (error) {
          console.error('Failed to unpublish camera track:', error);
        }
      }
      else if (!livekitCameraActive) {
        // If camera is not active, ensure we cleanup any existing camera tracks
        groupToggleCamera(false);
        await cleanupTracksBySource(Track.Source.Camera);
      }
    };
    try {
      manageCameraTrack();
    }
    catch (error) {
      console.error('Error managing camera track:', error);
      // If there's an error, we can handle it here
      // For example, we can dispatch an action to show an error message in the UI
      groupToggleCamera(false);
    }

  }, [room, livekitCameraActive, processedCameraTrack, getPublishedTracksBySource, roomState]);


  // Manage screen share track publishing
  useEffect(() => {
    if (!room || !roomState) {
      groupToggleScreenShare(false);
      return
    };
    const manageScreenShareTrack = async () => {
      if (livekitScreenShareActive && screenShareTrack && room) {
        // Proactive cleanup before publishing
        await cleanupTracksBySource(Track.Source.ScreenShare);

        try {
          const videoTrack = screenShareTrack.getVideoTracks()[0];
           const audioTrack = screenShareTrack.getAudioTracks()[0];
          await room.localParticipant.publishTrack(videoTrack, {
            name: 'screen',
            source: Track.Source.ScreenShare
          });
             if (audioTrack) {
       const publication = await room.localParticipant.publishTrack(audioTrack, {
  name: 'screen_audio',
  source: Track.Source.ScreenShareAudio // C'est la clé !
});

            console.log("🚀 Succès : LiveKit diffuse le son de l'écran !", publication.trackSid);
          } else {
            console.log("ℹ️ Partage d'écran actif sans audio (case non cochée)");
          } 
          if (livekitScreenShareRef.current === true)
            groupToggleScreenShare(true);
          console.log("screenShareTrack",screenShareTrack) ;
        } catch (error) {
          console.error('Failed to publish screen share track:', error);
          // FAILURE: Revert both livekit and UI states to OFF
          groupToggleScreenShare(false);
          dispatch(setTestModal("livekit_tester"));

        }
      } else if (!livekitScreenShareActive) {
        groupToggleScreenShare(false);
        await cleanupTracksBySource(Track.Source.ScreenShare);
      }
    };
    manageScreenShareTrack();
  }, [room, livekitScreenShareActive, screenShareTrack, roomState]);


  useEffect(() => {
    if (!micLoading) return;

    const timeoutId = setTimeout(() => {
      if (micLoading) {
        dispatch(setTestModal("livekit_tester"));
        groupToggleMic(false);

      }
    }, 10000); // 10 seconds

    return () => clearTimeout(timeoutId);
  }, [micLoading]);


  useEffect(() => {
    if (!cameraLoading) return;

    const timeoutId = setTimeout(() => {
      if (cameraLoading) {
        dispatch(setTestModal("livekit_tester"));
        groupToggleCamera(false);

      }
    }, 15000); // 15 seconds

    return () => clearTimeout(timeoutId); // cleanup if state changes before timeout
  }, [cameraLoading]);


  useEffect(() => {
    if (!screenShareLoading) return;

    const timeoutId = setTimeout(() => {
      if (screenShareLoading) {
        dispatch(setTestModal("livekit_tester"));
        groupToggleScreenShare(false);

      }
    }, 15000); // 15 seconds

    return () => clearTimeout(timeoutId); // cleanup if state changes before timeout
  }, [screenShareLoading]);

  useEffect(() => {
    activeGamesRef.current = activeGames;
  }, [activeGames]);


  useEffect(() => {
    if (!room || !roomState) return;

    const onDataReceived = (payload, participant) => {
      const decoder = new TextDecoder();
      const data = JSON.parse(decoder.decode(payload));
      if (data.type === 'GAME_INVITE') {
        dispatch(setIncomingInvite({
          fromName: data.fromName,
          game: data.game,
          senderIdentity: participant.identity
        }));
      }
      if (data.type === 'GAME_ACCEPTED') {
        const myId = room.localParticipant.identity;
        const amIPlayer1 = (myId === data.player1);
        const amIPlayer2 = (myId === data.player2);

        const isSpectator = (!amIPlayer1 && !amIPlayer2);


        dispatch(addActiveGame({
          type: data.game,
          gameId: data.gameId,
          player1Id: data.player1,
          player2Id: data.player2,
          player1Name: data.player1Name,
          player2Name: data.player2Name,
          isSpectator: isSpectator,
          roomId: room.name
        }));

      }
      if (data.type === 'GAME_DECLINED') {
        alert(`${data.fromName} a refusé votre invitation.`);
      }
      if (data.type === 'GET_GAME_STATE') {
        const currentGames = activeGamesRef.current;
        const myId = room.localParticipant.identity;
        const requesterId = participant.identity;
        if (requesterId === myId) return;

        currentGames.forEach(game => {
          let shouldISend = false;
          if (game.type === 'TICTACTOE') {
            if (game.player1Id === myId) shouldISend = true;
          }
          else if (game.type === 'SURVEY') {
            if (game.player1Id === myId) {
              shouldISend = true;
            }
            else {
              const ownerId = game.player1Id;
              const ownerIsOnline = room.getParticipantByIdentity(ownerId);
              const isOwnerRequesting = (participant.identity === ownerId);


              if (!ownerIsOnline || isOwnerRequesting) {
                const allParticipants = [room.localParticipant, ...Array.from(room.remoteParticipants.values())];
                const candidates = allParticipants.filter(p => p.identity !== requesterId);
                candidates.sort((a, b) => a.identity.localeCompare(b.identity));
                if (candidates.length > 0 && candidates[0].identity === myId) {
                  shouldISend = true;
                }
              }
            }

          }

          if (shouldISend) {
            let payloadToSend = null;
            if (game.type === 'TICTACTOE' && game.player1Id === myId) {
              payloadToSend = {
                type: 'GAME_SYNC',
                gameId: game.gameId,
                game: game.type,
                player1: game.player1Id,
                player2: game.player2Id,
                player1Name: game.player1Name,
                player2Name: game.player2Name,
                board: game.board,
                isXNext: game.isXNext,
                winner: game.winner
              };
            }
            else if (game.type === 'SURVEY') {
              payloadToSend = {
                type: 'SURVEY_OPEN',
                game: 'SURVEY',
                gameId: game.gameId,
                roomId: game.roomId,
                player1Id: game.player1Id,
                ownerName: game.ownerName,
                question: game.question,
                options: game.options,
                isMultiple: game.isMultiple,
                votes: game.votes,
                isClosed: game.isClosed
              };
            }
            if (payloadToSend) {
              const encoder = new TextEncoder();
              const syncData = encoder.encode(JSON.stringify(payloadToSend));
              room.localParticipant.publishData(syncData, {
                reliability: DataPacket_Kind.RELIABLE,
                destinationIdentities: [participant.identity]
              });
            }
          }


        });
      }
      if (data.type === 'GAME_SYNC') {
        if (!activeGames.find(g => g.gameId === data.gameId)) {
          const myId = room.localParticipant.identity;
          const isSpectator = (myId !== data.player1 && myId !== data.player2);

          dispatch(addActiveGame({
            type: data.game,
            gameId: data.gameId,
            player1Id: data.player1,
            player2Id: data.player2,
            player1Name: data.player1Name,
            player2Name: data.player2Name,
            isSpectator: isSpectator,
            board: data.board,
            isXNext: data.isXNext,
            winner: data.winner,
            roomId: room.name
          }));
        }
      }
      if (data.type === 'SURVEY_OPEN') {
        if (data.roomId !== room.name) return;

        const isowner = (data.player1Id === room.localParticipant.identity);
        const currentGames = activeGamesRef.current;
        if (!currentGames.find(g => g.gameId === data.gameId)) {
          dispatch(addActiveGame({
            type: data.game,
            gameId: data.gameId,
            roomId: data.roomId,
            player1Id: data.player1Id,
            ownerName: data.ownerName,
            question: data.question,
            options: data.options,
            isMultiple: data.isMultiple,
            votes: data.votes || {},
            isClosed: false,
            isSpectator: !isowner
          }));
        }
      }
      if (data.type === 'VOTE_CAST') {
        if (data.roomId !== room.name) return;


        dispatch(castVote({
          gameId: data.gameId,
          voterId: data.voterId,
          voterName: data.voterName,
          selectedOptions: data.selectedOptions
        }));
      }
      if (data.type === 'GAME_CLOSE') {
        dispatch(removeActiveGame(data.gameId));
      }

    };
    const verifyGamePlayers = (participant) => {
      const currentGames = activeGamesRef.current;
      console.log(currentGames, participant.identity);
      currentGames.forEach(game => {
        if ((game.player1Id === participant.identity || game.player2Id === participant.identity) && game.type !== 'SURVEY') {

          dispatch(removeActiveGame(game.gameId));
        }
      });
    }

    room.on('dataReceived', onDataReceived);
    room.on(RoomEvent.ParticipantDisconnected, verifyGamePlayers);
    const timer = setTimeout(async () => {
      const encoder = new TextEncoder();
      const requestData = encoder.encode(JSON.stringify({
        type: 'GET_GAME_STATE'
      }));

      try {
        await room.localParticipant.publishData(requestData, {
          reliability: DataPacket_Kind.RELIABLE,
          destinationIdentities: [] // J'envoie à tout le monde
        });
      } catch (e) {
        console.error("Erreur lors de la demande de sync:", e);
      }
    }, 1000);
    return () => {
      room.off('dataReceived', onDataReceived);
      room.off(RoomEvent.ParticipantDisconnected, verifyGamePlayers);
      clearTimeout(timer);
      dispatch(clearRoomGames(room.name));
    };
  }, [room, roomState]);

  useEffect(() => {
    if (!room || !surveyDataToPublish || !roomState) return;
    const sendSurvey = async () => {

      const gameId = `vote_${Date.now()}_${room.localParticipant.identity}`;

      const surveyPayload = {
        type: 'SURVEY_OPEN',
        game: 'SURVEY',
        gameId: gameId,
        roomId: room.name,
        player1Id: room.localParticipant.identity,
        ownerName: localAvatar?.display_name,
        question: surveyDataToPublish.question,
        options: surveyDataToPublish.options,
        isMultiple: surveyDataToPublish.isMultiple,
        votes: {},
        isClosed: false
      };
      dispatch(addActiveGame({
        ...surveyPayload,
        type: 'SURVEY',
        isSpectator: false
      }));
      const encoder = new TextEncoder();
      const data = encoder.encode(JSON.stringify(surveyPayload));

      try {
        await room.localParticipant.publishData(data, {
          reliability: DataPacket_Kind.RELIABLE,
          destinationIdentities: []
        });
        console.log("✅ Sondage envoyé au réseau");
      } catch (e) {
        console.error("❌ Erreur envoi sondage:", e);
      }
      dispatch(clearSurveyData());
    };
    sendSurvey();
  }, [room, surveyDataToPublish, roomState]);


  useEffect(() => {
    if (!room || !roomState) return;

    const isRoomSwitch = currentRoom?.room_id !== prevRoomIdRef.current;
    prevRoomIdRef.current = currentRoom?.room_id;

    if (isRoomSwitch) {
      dispatch(closeSessionModal());
    }
    else if (currentRoom?.chair_count === 2 && room.remoteParticipants.size === 0 && sessionView !== "progress") {
      dispatch(setSessionView("create"));
    }

  }, [room, roomState, currentRoom?.room_id, localAvatar?.chair_id, room?.remoteParticipants?.size]);

  useEffect(() => {
    if (!room || !roomState) return;
    if (room?.state === "connected" && currentRoom?.chair_count !== null) {
      if (currentRoom?.chair_count === 2 && room.remoteParticipants.size === 0)
        dispatch(setSessionView("create"));
    }
  }, [room?.remoteParticipants?.size , room?.state, roomState]);

  useEffect(() => {
    if (!room || !roomState) return;
    if (room.remoteParticipants.size > 0) {
      dispatch(closeSessionModal());
    }
  }, [room?.remoteParticipants?.size]);

 useEffect(() => {
  if (room) {
    livekitRoomRef.current = room;
  }
}, [room]);
  return (
    <>
      <div className="livekit-room-component" style={{ display: 'none' }}>
        {/* Debug information (can be removed in production) */}
        {process.env.NODE_ENV === 'development' && (
          <div>
            <div>Status: {roomState ? 'Connected' : 'Disconnected'}</div>
            <div>Room State: {room?.state}</div>
            {connectionError && <div>Error: {connectionError}</div>}
            <div>
              Audio Tracks: {room?.localParticipant?.audioTrackPublications.size || 0}
            </div>
            <div>
              Video Tracks: {room?.localParticipant?.videoTrackPublications.size || 0}
            </div>
          </div>
        )}

        {/* Hidden elements for camera effects processing */}
        <video
          ref={hiddenVideoRef}
          style={{
            position: 'absolute',
            top: '-9999px',
            left: '-9999px',
          }}
          muted
          playsInline
        />
        <canvas
          ref={hiddenCanvasRef}
          style={{
            position: 'absolute',
            top: '-9999px',
            left: '-9999px',
          }}
        />
        {selectedBackground && (
          <img
            ref={backgroundImageRef}
            src={selectedBackground}
            crossOrigin="anonymous"
            onLoad={() => setIsImageReady(true)}
            onError={() => setIsImageReady(false)}
            style={{
              position: 'absolute',
              top: '-9999px',
              left: '-9999px',
            }}
            alt="Background"
          />
        )}


      </div>
      <MediaDisplay
        remoteParticipants={remoteParticipants}
        localCameraTrack={cameraActive ? processedCameraTrack : null}
        localScreenShareTrack={screenShareActive && screenShareTrack ? screenShareTrack.getVideoTracks()[0] : null}
        onRemoteScreenShare={() => { }}
        room={room}
      />
      {/* Audio playback for remote audio tracks */}
      <AudioPlayback remoteAudioTracks={remoteAudioTracks} />
      {(
        <VoiceTranscription room={room} />
      )}

    </>
  );
}
